/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui';
import Button from '@/shared/ui/components/basic/Button';

export interface LaunchConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  destructive?: boolean;
}

/**
 * Confirm dialog for destructive launch actions (close, force release, sold).
 *
 * @param props - Open state, copy, and handlers.
 * @returns Dialog element.
 */
export function LaunchConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onOpenChange,
  onConfirm,
  destructive = true,
}: LaunchConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="launch-confirm-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button data-testid="launch-confirm-cancel" variant="outline">
              {cancelLabel}
            </Button>
          </DialogClose>
          <Button
            data-testid="launch-confirm-accept"
            onClick={onConfirm}
            className={
              destructive
                ? 'bg-(--status-error-text) hover:bg-(--status-error-text)/90 text-(--text-inverse)'
                : undefined
            }
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export interface LaunchRevertSoldDialogProps {
  open: boolean;
  unitLabel: string;
  clientLabel?: string | null;
  brokerLabel?: string | null;
  priceLabel?: string | null;
  /** Word the user must type (case-insensitive, trimmed) to enable the confirm button. */
  confirmWord?: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

/**
 * Typed-confirmation dialog for reverting a sold unit — the launch area's
 * one irreversible action. Requires the user to type `confirmWord` before
 * the destructive button enables, matching the design prototype's
 * "Irreversível" pattern for `revert_launch_sale`.
 *
 * @param props - Sale context, the required confirmation word, and handlers.
 * @returns Dialog element.
 */
export function LaunchRevertSoldDialog({
  open,
  unitLabel,
  clientLabel,
  brokerLabel,
  priceLabel,
  confirmWord = 'REVERTER',
  onOpenChange,
  onConfirm,
}: LaunchRevertSoldDialogProps) {
  const { t } = useTranslation('features/launches');
  const [typed, setTyped] = useState('');
  const enabled = typed.trim().toUpperCase() === confirmWord.toUpperCase();

  const handleOpenChange = (next: boolean) => {
    if (!next) setTyped('');
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="launch-revert-sold-dialog"
        className="launch-scope max-w-md overflow-hidden p-0"
      >
        <div
          className="flex flex-col gap-1 px-[var(--space-5)] py-[var(--space-4)]"
          style={{ background: 'var(--seed-base)', color: 'var(--launch-danger-banner-fg)' }}
        >
          <span
            className="text-[length:var(--text-meta)] font-[var(--font-medium)] tracking-[var(--tracking-wide)]"
            style={{ color: 'var(--launch-danger-banner-fg-muted)' }}
          >
            {t('revertDialog.irreversibleTag')}
          </span>
          <DialogTitle className="!text-[var(--launch-danger-banner-fg)]">
            {t('revertDialog.title', { unitLabel })}
          </DialogTitle>
        </div>

        <div className="flex flex-col gap-[var(--space-4)] px-[var(--space-5)] py-[var(--space-4)]">
          <DialogDescription className="text-[var(--text-secondary)]">
            {t('revertDialog.description')}
          </DialogDescription>

          {(clientLabel || brokerLabel || priceLabel) && (
            <div
              className="grid grid-cols-[auto_1fr] gap-x-[var(--space-3)] gap-y-[var(--space-1)] rounded-(--radius-md) p-[var(--space-3)] text-[length:var(--text-small)]"
              style={{ background: 'var(--surface-tertiary)', border: '1px solid var(--border-subtle)' }}
            >
              {clientLabel && (
                <>
                  <div className="text-[length:var(--text-meta)] text-[var(--text-tertiary)]">{t('revertDialog.soldTo')}</div>
                  <div className="text-[var(--text-primary)]">{clientLabel}</div>
                </>
              )}
              {brokerLabel && (
                <>
                  <div className="text-[length:var(--text-meta)] text-[var(--text-tertiary)]">{t('revertDialog.broker')}</div>
                  <div className="text-[var(--text-primary)]">{brokerLabel}</div>
                </>
              )}
              {priceLabel && (
                <>
                  <div className="text-[length:var(--text-meta)] text-[var(--text-tertiary)]">{t('revertDialog.price')}</div>
                  <div className="text-[var(--text-primary)]">{priceLabel}</div>
                </>
              )}
            </div>
          )}

          <div className="flex flex-col gap-[var(--space-2)]">
            <label className="text-[length:var(--text-small)] font-[var(--font-semibold)] text-[var(--text-secondary)]">
              {t('revertDialog.typeToEnable', { word: confirmWord })}
            </label>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={confirmWord}
              className="rounded-(--radius-md) border px-[var(--space-3)] py-[var(--space-2)] text-[length:var(--text-body-lg)] outline-none"
              style={{
                border: '1px solid var(--border-medium)',
                background: 'var(--surface-primary)',
              }}
            />
          </div>

          <div className="flex items-center justify-end gap-[var(--space-2)]">
            <DialogClose asChild>
              <Button variant="outline">{t('revertDialog.keepSale')}</Button>
            </DialogClose>
            <Button
              data-testid="launch-revert-sold-confirm"
              disabled={!enabled}
              onClick={() => {
                onConfirm();
                setTyped('');
              }}
              className={enabled ? undefined : 'cursor-not-allowed opacity-50'}
              style={enabled ? { background: 'var(--launch-danger-fg)', borderColor: 'var(--launch-danger-fg)', color: 'var(--text-inverse)' } : undefined}
            >
              {t('revertDialog.confirmButton')}
            </Button>
          </div>

          <span className="text-[length:var(--text-meta)] text-[var(--text-tertiary)]">
            {t('revertDialog.footerNote')}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
